import React from 'react'

interface UseAutoClearTimerOptions {
  duration?: number
  onClear?: () => void
  autoStart?: boolean
}

interface UseAutoClearTimerReturn {
  start: () => void
  stop: () => void
  reset: () => void
  isActive: boolean
  timeRemaining: number
}

export const useAutoClearTimer = (options: UseAutoClearTimerOptions = {}): UseAutoClearTimerReturn => {
  const { duration = 25000, onClear, autoStart = false } = options

  const [isActive, setIsActive] = React.useState(false)
  const [timeRemaining, setTimeRemaining] = React.useState(duration)
  const timerRef = React.useRef<NodeJS.Timeout | null>(null)
  const startTimeRef = React.useRef<number | null>(null)
  const onClearRef = React.useRef<(() => void) | undefined>(onClear)

  // Update onClearRef when onClear prop changes
  React.useEffect(() => {
    onClearRef.current = onClear
  }, [onClear])

  const start = React.useCallback(() => {
    if (isActive) return // Already running

    setIsActive(true)
    setTimeRemaining(duration)
    startTimeRef.current = Date.now()

    timerRef.current = setTimeout(() => {
      onClearRef.current?.()
      setIsActive(false)
      setTimeRemaining(0)
      startTimeRef.current = null
    }, duration)
  }, [duration])

  const stop = React.useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    setIsActive(false)
    startTimeRef.current = null
  }, [])

  const reset = React.useCallback(() => {
    stop()
    setTimeRemaining(duration)
  }, [duration])

  // Update time remaining every 100ms for UI feedback
  React.useEffect(() => {
    if (!isActive || !startTimeRef.current) return

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current!
      const remaining = Math.max(0, duration - elapsed)
      setTimeRemaining(remaining)

      if (remaining <= 0) {
        clearInterval(interval)
      }
    }, 100)

    return () => clearInterval(interval)
  }, [isActive, duration])

  // Auto-start if enabled
  React.useEffect(() => {
    if (autoStart) {
      start()
    }

    return () => {
      stop()
    }
  }, [autoStart])

  // Cleanup on unmount
  React.useEffect(
    () => () => {
      stop()
    },
    [],
  )

  return {
    start,
    stop,
    reset,
    isActive,
    timeRemaining,
  }
}
